import { useEffect, useState } from "react";
import { Plus, RotateCcw, Trash2, Upload, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { uploadImage } from "@/lib/upload";
import { RichTextEditor } from "@/components/RichTextEditor";
import ReviewsManager from "@/pages/AdminReviews";

const PAGES = [["home", "Home page", "/"], ["about", "About Jawai", "/about"], ["safaris", "Safaris", "/safaris"], ["stays", "Stays", "/stays"], ["experiences", "Experiences", "/experiences"], ["reviews", "Reviews", "/"]];

const T = (k, label, type = "text", max) => ({ k, label, type, max });
const SEO = { title: "SEO (Google & social sharing)", path: "seo", fields: [T("meta_title", "Page title in Google", "text", 60), T("meta_description", "Description in Google", "textarea", 160), T("og_image", "Share image (WhatsApp / Facebook)", "image")] };
const SCHEMAS = {
  home: [
    SEO,
    { title: "Hero (top banner)", path: "hero", fields: [T("eyebrow", "Small label"), T("title", "Main heading (H1)", "text", 70), T("subtitle", "Intro text", "textarea"), T("image", "Banner photo", "image"), T("image_alt", "Photo description (alt text)")] },
    { title: "Trust strip", path: "trust", list: { fields: [T("title", "Title"), T("sub", "Subtitle")], max: 4, blank: { title: "", sub: "" } } },
    { title: "Section headings", path: "sections", fields: [T("safaris_eyebrow", "Safaris label"), T("safaris_title", "Safaris heading"), T("stays_eyebrow", "Stays label"), T("stays_title", "Stays heading"), T("packages_eyebrow", "Packages label"), T("packages_title", "Packages heading")] },
    { title: "How to book", path: "steps", fields: [T("eyebrow", "Small label"), T("title", "Heading")], listKey: "items", list: { fields: [T("text", "Step")], max: 5, blank: { text: "" } } },
    { title: "About Jawai section", path: "destination", fields: [T("eyebrow", "Small label"), T("title", "Heading"), T("body_html", "Text", "rich"), T("image1", "Photo 1", "image"), T("image1_alt", "Photo 1 description"), T("image2", "Photo 2", "image"), T("image2_alt", "Photo 2 description")] },
    { title: "FAQ (also shown as FAQ results in Google)", path: "faq", fields: [T("eyebrow", "Small label"), T("title", "Heading")], listKey: "items", list: { fields: [T("q", "Question"), T("a", "Answer", "textarea")], max: 20, blank: { q: "", a: "" } } },
  ],
  safaris: [SEO, { title: "Safaris page intro", path: "intro", fields: [T("eyebrow", "Small label"), T("title", "Main heading (H1)", "text", 70), T("subtitle", "Intro text", "textarea"), T("image", "Banner photo (optional)", "image"), T("image_alt", "Photo description (alt text)")] }],
  stays: [SEO, { title: "Stays page intro", path: "intro", fields: [T("eyebrow", "Small label"), T("title", "Main heading (H1)", "text", 70), T("subtitle", "Intro text", "textarea"), T("image", "Banner photo (optional)", "image"), T("image_alt", "Photo description (alt text)")] }],
  experiences: [SEO, { title: "Experiences page intro", path: "intro", fields: [T("eyebrow", "Small label"), T("title", "Main heading (H1)", "text", 70), T("subtitle", "Intro text", "textarea"), T("image", "Banner photo (optional)", "image"), T("image_alt", "Photo description (alt text)")] }],
  about: [
    SEO,
    { title: "About Jawai page", path: "page", fields: [T("eyebrow", "Small label"), T("title", "Main heading (H1)", "text", 70), T("image", "Main photo", "image"), T("image_alt", "Photo description (alt text)"), T("body_html", "Page content", "rich")] },
  ],
};
const input = "h-10 w-full rounded-md border border-[#E2DACF] bg-white px-3 text-sm";

function ImageField({ value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { onChange(await uploadImage(f)); toast.success("Photo uploaded"); } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <div className="flex gap-3 items-start">
      <div className="w-32 h-20 rounded-lg bg-[#EFE9E1] overflow-hidden shrink-0">{value && <img src={value} alt="" className="w-full h-full object-cover"/>}</div>
      <div className="flex-1 space-y-2">
        <label className="jt-pill-outline text-xs cursor-pointer inline-flex"><Upload size={13}/> {busy ? "Uploading…" : "Upload photo"}
          <input type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${testid}-file`}/></label>
        <input className={input} placeholder="…or paste an image URL" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}/>
      </div>
    </div>
  );
}

function FieldInput({ f, value, onChange, testid }) {
  const count = f.max ? <span className={value.length > f.max ? "text-[#C86D51]" : ""}> ({value.length}/{f.max})</span> : null;
  return (
    <label className="block text-xs text-[#5C6B63] space-y-1">
      <span>{f.label}{count}</span>
      {f.type === "text" && <input className={input} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}/>}
      {f.type === "textarea" && <textarea rows={3} className="w-full rounded-md border border-[#E2DACF] p-2 text-sm" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}/>}
      {f.type === "image" && <ImageField value={value} onChange={onChange} testid={testid}/>}
      {f.type === "rich" && <div className="text-sm text-[#1C2421]"><RichTextEditor value={value} onChange={onChange}/></div>}
    </label>
  );
}

function ListEditor({ spec, items, onChange, testid }) {
  const set = (i, k, v) => onChange(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="rounded-xl bg-[#FBF8F4] ring-1 ring-black/5 p-3 grid gap-2 md:grid-cols-[1fr_auto]" data-testid={`${testid}-item-${i}`}>
          <div className="grid gap-2">{spec.fields.map((f) => <FieldInput key={f.k} f={f} value={it[f.k] || ""} onChange={(v) => set(i, f.k, v)} testid={`${testid}-${i}-${f.k}`}/>)}</div>
          <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="self-start p-2" aria-label="Remove" data-testid={`${testid}-remove-${i}`}><Trash2 size={14} className="text-[#C86D51]"/></button>
        </div>
      ))}
      {items.length < spec.max && <button type="button" onClick={() => onChange([...items, { ...spec.blank }])} className="text-xs text-[#1E3A2B] inline-flex items-center gap-1" data-testid={`${testid}-add`}><Plus size={13}/> Add</button>}
    </div>
  );
}

function Group({ g, data, setData, page }) {
  const val = data[g.path];
  const tid = `content-${page}-${g.path}`;
  const setField = (k, v) => setData({ ...data, [g.path]: { ...val, [k]: v } });
  return (
    <section className="bg-white ring-1 ring-black/5 rounded-2xl p-5 space-y-3" data-testid={tid}>
      <h3 className="font-serif text-lg text-[#1E3A2B]">{g.title}</h3>
      {g.fields && <div className="grid gap-3 md:grid-cols-2">{g.fields.map((f) => (
        <div key={f.k} className={["rich", "textarea", "image"].includes(f.type) ? "md:col-span-2" : ""}>
          <FieldInput f={f} value={val[f.k] || ""} onChange={(v) => setField(f.k, v)} testid={`${tid}-${f.k}`}/>
        </div>))}</div>}
      {g.list && <ListEditor spec={g.list} items={g.listKey ? val[g.listKey] : val} testid={`${tid}-list`}
        onChange={(items) => (g.listKey ? setField(g.listKey, items) : setData({ ...data, [g.path]: items }))}/>}
    </section>
  );
}

export default function ContentManager() {
  const [page, setPage] = useState("home");
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setData(null);
    if (page === "reviews") return;
    let live = true; // ignore stale responses after switching page (prevents rendering one page's schema with another's data)
    api.get(`/content/${page}`).then((r) => { if (live) setData(r.data); }).catch((e) => { if (live) toast.error(formatErr(e)); });
    return () => { live = false; };
  }, [page]);
  const save = async () => {
    setSaving(true);
    try { const { data: d } = await api.put(`/admin/content/${page}`, data); setData(d); toast.success("Saved. Changes are live."); }
    catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset this page to the original content? Your edits will be lost.")) return;
    const { data: d } = await api.delete(`/admin/content/${page}`); setData(d); toast.success("Reset to original");
  };

  return (
    <div className="mt-6 space-y-5" data-testid="content-admin">
      <div className="flex flex-wrap items-center gap-3 sticky top-0 z-20 bg-[#FBF8F4]/95 backdrop-blur py-3">
        {PAGES.map(([k, l]) => (
          <button key={k} onClick={() => { if (k !== page) { setData(null); setPage(k); } }} className={`px-4 py-2 rounded-full text-sm ${page === k ? "bg-[#1E3A2B] text-white" : "bg-white ring-1 ring-black/5"}`} data-testid={`content-page-${k}`}>{l}</button>
        ))}
        <a href={PAGES.find((x) => x[0] === page)[2]} target="_blank" rel="noreferrer" className="text-xs text-[#5C6B63] inline-flex items-center gap-1" data-testid="content-view"><ExternalLink size={13}/> View page</a>
        <span className="flex-1"/>
        {page !== "reviews" && <>
        {data?.updated_at && <span className="text-xs text-[#5C6B63]">Last saved {new Date(data.updated_at).toLocaleString("en-IN")}</span>}
        <button onClick={reset} className="jt-pill-outline text-sm" data-testid="content-reset"><RotateCcw size={13}/> Reset</button>
        <button onClick={save} disabled={!data || saving} className="jt-pill-primary text-sm disabled:opacity-40" data-testid="content-save">{saving ? "Saving…" : "Save changes"}</button>
        </>}
      </div>
      {page === "reviews" ? <ReviewsManager/> : !data ? <p className="text-sm text-[#5C6B63]">Loading…</p> : SCHEMAS[page].map((g) => <Group key={`${page}-${g.path}`} g={g} data={data} setData={setData} page={page}/>)}
    </div>
  );
}
